(() => {
  const WRAPPER_SELECTOR = '.js-load-more-wrapper';
  const PRODUCTS_CONTAINER_SELECTOR = '.js-collection-results';

  let isLoading = false;
  let observer = null;
  let observedEl = null;
  let lastRequestedUrl = null;

  function getWrapper() {
    return document.querySelector(WRAPPER_SELECTOR);
  }

  function getProductsContainer(root = document) {
    return root.querySelector(PRODUCTS_CONTAINER_SELECTOR);
  }

  function ensureObserver() {
    if (observer) return;

    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          const wrapper = entry.target;
          loadMoreProducts(wrapper);
        }
      },
      {
        root: null,
        rootMargin: '200px',
        threshold: 0
      }
    );
  }

  function observeCurrentWrapper() {
    ensureObserver();

    const wrapper = getWrapper();

    if (!wrapper) {
      if (observedEl) {
        observer.unobserve(observedEl);
        observedEl = null;
      }
      return;
    }

    if (observedEl && observedEl !== wrapper) {
      try { observer.unobserve(observedEl); } catch (e) {}
      observedEl = null;
    }

    if (!observedEl) {
      observedEl = wrapper;
      observer.observe(wrapper);
    }
  }

  async function loadMoreProducts(wrapper) {
    if (isLoading) return;

    const nextUrl = wrapper?.dataset?.url;

    if (!nextUrl) {
      stopObservingAndCleanup(wrapper);
      return;
    }

    if (lastRequestedUrl === nextUrl) return;
    lastRequestedUrl = nextUrl;

    isLoading = true;

    try {
      const res = await fetch(nextUrl, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('Fetch failed');

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newProductsContainer = getProductsContainer(doc);
      const currentProductsContainer = getProductsContainer(document);

      if (newProductsContainer && currentProductsContainer) {
        currentProductsContainer.insertAdjacentHTML('beforeend', newProductsContainer.innerHTML);

        if (typeof window.initializeObservers === 'function') {
          window.initializeObservers();
        }
        if (typeof initCollectionBanners === "function") {
          initCollectionBanners();
        }
        if (typeof window.initHeroProducts === "function") {
          window.initHeroProducts();
        }
      }

      const newWrapper = doc.querySelector(WRAPPER_SELECTOR);

      if (newWrapper && newWrapper.dataset && newWrapper.dataset.url) {
        const liveWrapper = getWrapper();

        if (liveWrapper) {
          liveWrapper.dataset.url = newWrapper.dataset.url;

          const liveLink = liveWrapper.querySelector('a');
          if (liveLink) liveLink.setAttribute('href', newWrapper.dataset.url);
        }
      } else {
        stopObservingAndCleanup(getWrapper() || wrapper);
      }

      observeCurrentWrapper();
    } catch (e) {
      console.error('Load more error:', e);
      stopObservingAndCleanup(getWrapper() || wrapper);
    } finally {
      isLoading = false;
    }
  }

  function stopObservingAndCleanup(wrapper) {
    if (!observer) return;

    if (wrapper) {
      try { observer.unobserve(wrapper); } catch (e) {}
      if (observedEl === wrapper) observedEl = null;

      if (wrapper.isConnected) wrapper.remove();
    }
  }

  function setupMutationAutoRefresh() {
    const root = document.querySelector('main') || document.body;
    if (!root) return;

    const mo = new MutationObserver(() => {
      requestAnimationFrame(observeCurrentWrapper);
    });

    mo.observe(root, { childList: true, subtree: true });
  }

  function init() {
    observeCurrentWrapper();
    setupMutationAutoRefresh();
  }

  document.addEventListener('DOMContentLoaded', init);

  window.CollectionInfiniteScroll = {
    refresh: observeCurrentWrapper
  };
})();

document.addEventListener('DOMContentLoaded', function () {
  const facets = document.querySelector('collection-facets');
  const sticky = document.querySelector('.js-sticky-facets');

  if (!facets || !sticky) return;

  let hideTimeout = null;
  const TOP_VH = 0.3;

  function inTop30vhZone() {
    const f = facets.getBoundingClientRect();
    const line = window.innerHeight * TOP_VH;
    return f.top <= line && f.bottom >= line;
  }

  function stickyOutsideBottom() {
    const f = facets.getBoundingClientRect();
    const s = sticky.getBoundingClientRect();
    return s.bottom > f.bottom;
  }

  function clearHideTimer() {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }
  }

  function scheduleHideIfStillOutside() {
    if (hideTimeout) return;
    hideTimeout = setTimeout(() => {
      if (!inTop30vhZone() || stickyOutsideBottom()) {
        sticky.classList.add('hide');
      }
      hideTimeout = null;
    }, 200);
  }

  function check() {
    if (!inTop30vhZone()) {
      scheduleHideIfStillOutside();
      return;
    }

    if (stickyOutsideBottom()) {
      scheduleHideIfStillOutside();
    } else {
      clearHideTimer();
      sticky.classList.remove('hide');
    }
  }

  check();
  window.addEventListener('scroll', check, { passive: true });
  window.addEventListener('resize', check);
});

window.initCollectionBanners = function (root = document) {
  root.querySelectorAll(".js-collection-banners").forEach((wrap) => {
    if (wrap.dataset.collectionBannersInited === "1") return;
    wrap.dataset.collectionBannersInited = "1";

    const dots = [...wrap.querySelectorAll(".js-dot")];
    const banners = [...wrap.querySelectorAll(".js-collection-banner")];
    if (!banners.length) return;

    let i = 0;
    let lock = false;

    function go(n) {
      if (lock) return;
      lock = true;

      const prev = i;
      i = (n + banners.length) % banners.length;

      const prevB = banners[prev];
      const nextB = banners[i];

      if (prevB && prevB !== nextB) prevB.classList.remove("active");
      nextB.classList.add("active");

      dots.forEach((d, idx) => d.classList.toggle("active", idx === i));

      requestAnimationFrame(() => (lock = false));
    }

    function next() {
      go(i + 1);
    }

    banners.forEach((b, idx) => b.classList.toggle("active", idx === 0));
    dots.forEach((d, idx) => d.classList.toggle("active", idx === 0));

    dots.forEach((d, idx) => d.addEventListener("click", () => go(idx)));

    wrap.addEventListener("animationend", (e) => {
      const activeDot = wrap.querySelector(".js-dot.active");
      if (!activeDot) return;
      if (e.target.closest(".js-dot") !== activeDot) return;
      next();
    });
  });
};

document.addEventListener("DOMContentLoaded", () => {
  initCollectionBanners();
});